"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.prepareAppBaseFileAddresses = prepareAppBaseFileAddresses;
exports.rewriteAppBaseCssUrls = rewriteAppBaseCssUrls;
const app_base_contract_1 = require("./app-base-contract");
const isModuleFile = (file) => /^(?:application|text)\/(?:javascript|ecmascript)$/.test(file.contentType.split(';')[0]) || /\.m?js$/i.test(file.path);
/** Immutable file identities prepared once for an admitted package state.
 * Hosts keep their existing file/DOM readers; neither application JS nor its
 * imports are rewritten. CSS identity includes the raw identities of its
 * transitive dependencies, including cycles, before any URLs are projected.
 * With `moduleImports` a page names the whole static module chain of its
 * entry modules up front, so the browser does not discover it level by
 * level; a host serving from a local cache passes none. */
async function prepareAppBaseFileAddresses(files, readText, basePath = '', moduleImports) {
    const inventory = new Map();
    for (const file of files) {
        if (!/^[a-f0-9]{64}$/i.test(file.sha256))
            throw new Error('App file has no valid sha256: ' + file.path);
        if (inventory.has(file.path))
            throw new Error('Duplicate App file: ' + file.path);
        inventory.set(file.path, {
            path: file.path,
            contentType: file.contentType,
            sha256: file.sha256,
        });
    }
    const localPath = (url, from, basePath = '') => {
        if (!url || /^(?:[a-z][a-z\d+.-]*:|\/\/|#)/i.test(url))
            return undefined;
        let local = url;
        if (url.startsWith('/') && basePath) {
            const prefix = basePath.replace(/\/$/, '') + '/';
            if (!url.startsWith(prefix))
                return undefined;
            local = url.slice(prefix.length);
            from = '';
        }
        const resolved = new URL(local, 'https://app.invalid/' + from);
        const filePath = decodeURIComponent(resolved.pathname.slice(1));
        return inventory.has(filePath) ? filePath : undefined;
    };
    const css = new Map();
    await Promise.all(files
        .filter(file => file.contentType.split(';')[0] === 'text/css')
        .map(async (file) => css.set(file.path, file.text ?? (await readText(file)))));
    const dependencies = new Map();
    for (const [filePath, text] of css) {
        const found = new Set();
        rewriteAppBaseCssUrls(text, url => {
            const target = localPath(url, filePath, basePath);
            if (target)
                found.add(target);
            return url;
        });
        dependencies.set(filePath, found);
    }
    // The local modules each module imports; imports of other origins or of
    // names the App does not ship are not the App's to name.
    const modules = new Map();
    const importedBy = (filePath, text) => [
        ...new Set(moduleImports(filePath, text).flatMap(url => localPath(url, filePath, basePath) ?? [])),
    ];
    if (moduleImports)
        await Promise.all(files
            .filter(isModuleFile)
            .map(async (file) => modules.set(file.path, importedBy(file.path, file.text ?? (await readText(file))))));
    const hashes = new Map(files.map(file => [file.path, file.sha256.toLowerCase()]));
    for (const filePath of css.keys()) {
        const seen = new Set();
        const pending = [filePath];
        while (pending.length) {
            const next = pending.pop();
            if (seen.has(next))
                continue;
            seen.add(next);
            pending.push(...(dependencies.get(next) ?? []));
        }
        hashes.set(filePath, (0, app_base_contract_1.appBaseSha256Hex)(JSON.stringify([...seen].sort().map(file => [file, inventory.get(file).sha256]))));
    }
    const version = (filePath) => {
        const hash = hashes.get(filePath);
        if (!hash)
            throw new Error('Unknown App file: ' + filePath);
        return hash.slice(0, 16);
    };
    const encodedPath = (filePath) => filePath.split('/').map(encodeURIComponent).join('/');
    /** The versioned address of a file of the App; below `filesPath` when the
     * host serves its files at an address of their own. */
    const address = (url, from, basePath = '', filesPath) => {
        const filePath = localPath(url, from, basePath);
        if (!filePath)
            return url;
        const hashAt = url.indexOf('#');
        const fragment = hashAt < 0 ? '' : url.slice(hashAt);
        const plain = hashAt < 0 ? url : url.slice(0, hashAt);
        const queryAt = plain.indexOf('?');
        const path = queryAt < 0 ? plain : plain.slice(0, queryAt);
        const params = new URLSearchParams(queryAt < 0 ? '' : plain.slice(queryAt + 1));
        params.set('v', version(filePath));
        return ((filesPath === undefined
            ? path
            : filesPath.replace(/\/$/, '') + '/' + encodedPath(filePath)) +
            '?' +
            params.toString() +
            fragment);
    };
    const importMap = (basePath, filesPath) => {
        const prefix = basePath.replace(/\/$/, '') + '/';
        const filesPrefix = filesPath === undefined ? prefix : filesPath.replace(/\/$/, '') + '/';
        const imports = Object.fromEntries([...inventory.values()].filter(isModuleFile).flatMap(file => {
            const path = encodedPath(file.path);
            const versioned = filesPrefix + path + '?v=' + version(file.path);
            return [...new Set([prefix + path, filesPrefix + path])].map(key => [
                key,
                versioned,
            ]);
        }));
        return JSON.stringify({ imports }).replace(/</g, '\\u003c');
    };
    /** The modules the entry modules of a page reach statically, nearest
     * first, each once; the entries themselves the page names already. */
    const modulePreloads = (document, filePath, pageBasePath) => {
        if (!moduleImports)
            return [];
        const entries = new Set();
        const pending = [];
        for (const node of document.querySelectorAll('script[type="module"]')) {
            const src = node.getAttribute('src');
            if (src === null)
                pending.push(...importedBy(filePath, node.textContent ?? ''));
            else {
                const entry = localPath(src, filePath, pageBasePath);
                if (entry) {
                    entries.add(entry);
                    pending.push(...(modules.get(entry) ?? []));
                }
            }
        }
        const found = [];
        const seen = new Set(entries);
        // The iteration also visits the modules pushed while it runs.
        for (const module of pending) {
            if (seen.has(module) || !modules.has(module))
                continue;
            seen.add(module);
            found.push(module);
            pending.push(...modules.get(module));
        }
        return found;
    };
    const representations = new Map([...css].map(([filePath, text]) => [
        filePath,
        rewriteAppBaseCssUrls(text, url => address(url, filePath, basePath)),
    ]));
    return {
        version,
        address,
        /** Only transformed CSS is held; other files keep their existing reader. */
        representation: (filePath) => representations.get(filePath),
        bytes: JSON.stringify(files.map(({ path, contentType, sha256 }) => ({
            path,
            contentType,
            sha256,
        }))).length *
            3 +
            [...representations.values()].reduce((sum, text) => sum + text.length * 3, 0),
        /** The import map of the App's modules, as a page carries it. */
        importMap,
        /**
         * Writes the versioned addresses of the files a page names and the import
         * map of its modules. With `filesPath` the page loads them from there, and
         * modules imported by their address below `basePath` too.
         */
        projectDocument(document, filePath, basePath, filesPath) {
            const project = (url) => address(url, filePath, basePath, filesPath);
            const preloads = modulePreloads(document, filePath, basePath);
            for (const node of document.querySelectorAll('script[src],link[href],img[src],source[src],video[poster],audio[src],video[src]')) {
                const attribute = node.hasAttribute('src')
                    ? 'src'
                    : node.hasAttribute('href')
                        ? 'href'
                        : 'poster';
                node.setAttribute(attribute, project(node.getAttribute(attribute)));
            }
            for (const node of document.querySelectorAll('img[srcset],source[srcset]'))
                node.setAttribute('srcset', rewriteAppBaseSrcset(node.getAttribute('srcset'), project));
            for (const node of document.querySelectorAll('style'))
                node.textContent = rewriteAppBaseCssUrls(node.textContent ?? '', project);
            for (const node of document.querySelectorAll('[style]'))
                node.setAttribute('style', rewriteAppBaseCssUrls(node.getAttribute('style'), project));
            const text = importMap(basePath, filesPath);
            for (const previous of document.querySelectorAll('script[type="importmap"][data-sl-file-map]'))
                previous.remove();
            const map = document.createElement('script');
            map.setAttribute('type', 'importmap');
            map.setAttribute('data-sl-file-map', '');
            map.textContent = text;
            for (const previous of document.querySelectorAll('link[rel="modulepreload"][data-sl-file-preload]'))
                previous.remove();
            document.head.prepend(map);
            let before = map.nextSibling;
            for (const module of preloads) {
                const link = document.createElement('link');
                link.setAttribute('rel', 'modulepreload');
                // The address the import map gives the module.
                link.setAttribute('href', (filesPath ?? basePath).replace(/\/$/, '') +
                    '/' +
                    encodedPath(module) +
                    '?v=' +
                    version(module));
                link.setAttribute('data-sl-file-preload', '');
                document.head.insertBefore(link, before);
            }
            return text;
        },
    };
}
function cssUrlText(text) {
    return text.replace(/\\(?:([a-f0-9]{1,6})\s?|(\r\n|[\n\r\f])|([\s\S]))/gi, (_match, hex, newline, escaped) => hex
        ? String.fromCodePoint(parseInt(hex, 16) > 0x10ffff ? 0xfffd : parseInt(hex, 16) || 0xfffd)
        : newline
            ? ''
            : escaped);
}
/** Skip comments and string literals; only real url()/@import references change. */
function rewriteAppBaseCssUrls(text, address) {
    const token = /\/\*[\s\S]*?\*\/|@import\s+(?:"((?:\\.|[^"\\])*)"|'((?:\\.|[^'\\])*)')|"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|\burl\(\s*(?:"((?:\\.|[^"\\])*)"|'((?:\\.|[^'\\])*)'|((?:\\.|[^)\\])*?))\s*\)/gi;
    return text.replace(token, (match, importDouble, importSingle, double, single, bare) => {
        const raw = importDouble ?? importSingle ?? double ?? single ?? bare;
        if (raw === undefined)
            return match;
        const value = cssUrlText(raw.trim());
        const next = address(value);
        if (next === value)
            return match;
        const quoted = JSON.stringify(next).replace(/</g, '\\3c ');
        return importDouble !== undefined || importSingle !== undefined
            ? '@import ' + quoted
            : 'url(' + quoted + ')';
    });
}
/** WHATWG candidate shape: commas inside a data URL belong to that URL. */
function rewriteAppBaseSrcset(text, address) {
    const candidates = [];
    let at = 0;
    while (at < text.length) {
        while (/[\s,]/.test(text[at] ?? '') && at < text.length)
            at++;
        if (at >= text.length)
            break;
        const start = at;
        while (at < text.length && !/\s/.test(text[at]))
            at++;
        const token = text.slice(start, at);
        const url = token.replace(/,+$/, '');
        if (token.endsWith(',')) {
            candidates.push(address(url));
            continue;
        }
        const descriptor = at;
        let parentheses = 0;
        while (at < text.length) {
            if (text[at] === '(')
                parentheses++;
            if (text[at] === ')')
                parentheses--;
            if (text[at] === ',' && parentheses === 0)
                break;
            at++;
        }
        candidates.push(address(url) + text.slice(descriptor, at));
        at++;
    }
    return candidates.join(', ');
}
